import { useState } from "react";
import { ReferralViewer } from "./ReferralViewer";
import { RejectionWarningModal } from "@/components/admin/RejectionWarningModal";
import { HiredConfirmationModal, type HireFormData } from "@/components/admin/HiredConfirmationModal";
import { ReferralStatus } from "@/lib/schema";
import { AdminReferralWithUI, addRecruiterNote } from "@/api/referrals";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { changeReferralStatus, changeReferralStatusToHired, getStatusDisplayName } from "@/lib/referralStatusUtils";
import { getJob } from "@/api/jobs";
import { getCvUploadBonusAmount } from "@/api/referralSettings";

interface ReferralManagerProps {
  // Core referral viewer props
  selectedReferral: AdminReferralWithUI | null;
  isReferralViewerOpen: boolean;
  onCloseReferralViewer: () => void;
  
  // Optional callbacks for custom behavior
  onStatusChangeSuccess?: (referralId: string, newStatus: ReferralStatus, hireData?: HireFormData) => void;
  onNoteAddSuccess?: (referralId: string, note: string) => void;
  onError?: (error: string) => void;
  
  // Optional context (e.g., jobId for JobDetails page)
  jobId?: string;
}

export function ReferralManager({
  selectedReferral,
  isReferralViewerOpen,
  onCloseReferralViewer,
  onStatusChangeSuccess,
  onNoteAddSuccess,
  onError,
  jobId
}: ReferralManagerProps) {
  const { toast } = useToast();
  const { userData } = useAuth();
  
  // Rejection warning modal state
  const [showRejectionWarning, setShowRejectionWarning] = useState(false);
  const [pendingRejection, setPendingRejection] = useState<{ referralId: string; candidateName: string } | null>(null);

  // Hired confirmation modal state
  const [showHiredConfirmation, setShowHiredConfirmation] = useState(false);
  const [pendingHire, setPendingHire] = useState<{ 
    referralId: string; 
    candidateName: string; 
    jobTitle?: string; 
    rewardAmount?: number;
    bonusAmount?: number;
    hasBonus?: boolean;
    totalAmount?: number;
  } | null>(null);

  // Processing states
  const [isProcessingHire, setIsProcessingHire] = useState(false);
  const [hireError, setHireError] = useState<string | null>(null);
  const [hireSuccess, setHireSuccess] = useState(false);
  const [isProcessingRejection, setIsProcessingRejection] = useState(false);
  const [rejectionError, setRejectionError] = useState<string | null>(null);
  const [rejectionSuccess, setRejectionSuccess] = useState(false);

  const handleStatusChange = (referralId: string, newStatus: ReferralStatus) => {
    // Find the referral being updated
    const referral = selectedReferral;
    if (!referral) return;
    
    const referralName = getCandidateName(referral);
    
    // Show warning modal for REJECTED status
    if (newStatus === ReferralStatus.REJECTED) {
      setPendingRejection({ referralId, candidateName: referralName });
      setShowRejectionWarning(true);
      return;
    }
    
    // Show confirmation modal for HIRED status
    if (newStatus === ReferralStatus.HIRED) {
      // Fetch both job data and bonus settings
      Promise.all([
        getJob(referral.jobId),
        referral.bonusPoints ? getCvUploadBonusAmount() : Promise.resolve(0)
      ]).then(([job, bonusAmount]) => {
        const baseReward = job?.rewardInAirPoints || referral.rewardAmount || 0;
        const hasBonus = Boolean(referral.bonusPoints);
        const bonus = hasBonus ? bonusAmount : 0;
        const totalAmount = baseReward + bonus;

        setPendingHire({ 
          referralId, 
          candidateName: referralName,
          jobTitle: referral.jobTitle,
          rewardAmount: baseReward,
          bonusAmount: bonus,
          hasBonus,
          totalAmount
        });
        setShowHiredConfirmation(true);
      }).catch((error) => {
        console.error('Error fetching hire confirmation data:', error);
        // Fallback to referral reward amount only
        const baseReward = referral.rewardAmount || 0;
        setPendingHire({ 
          referralId, 
          candidateName: referralName,
          jobTitle: referral.jobTitle,
          rewardAmount: baseReward,
          bonusAmount: 0,
          hasBonus: false,
          totalAmount: baseReward
        });
        setShowHiredConfirmation(true);
      });
      return;
    }
    
    // Process other status changes immediately
    processStatusChange(referralId, newStatus);
  };

  const getCandidateName = (referral: AdminReferralWithUI): string => {
    // AdminReferralWithUI already has candidateName computed
    return referral.candidateName || 'Unknown Candidate';
  };

  const processStatusChange = async (referralId: string, newStatus: ReferralStatus, rejectionNotes?: string): Promise<boolean> => {
    if (!userData) {
      const error = 'You must be logged in to update referral status.';
      onError?.(error);
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Authentication Error",
        description: error,
        variant: "destructive"
      });
      return false;
    }

    const success = await changeReferralStatus({
      referralId,
      newStatus,
      userId: userData.id,
      rejectionNotes,
      onSuccess: (referralId, newStatus) => {
        toast({
          title: "Status Updated",
          description: `Referral status has been updated to ${getStatusDisplayName(newStatus)}.`,
        });
        // Notify parent of successful status change
        onStatusChangeSuccess?.(referralId, newStatus);
      },
      onError: (errorMessage) => {
        onError?.(errorMessage);
        // TODO: Integrate with Sentry for error tracking and reporting
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive"
        });
      }
    });

    return success;
  };

  const processHiredStatusChange = async (referralId: string, hireData: HireFormData) => {
    if (!userData) {
      const error = 'You must be logged in to process hire.';
      setHireError(error);
      setIsProcessingHire(false);
      return;
    }

    setIsProcessingHire(true);
    setHireError(null); // Clear any previous errors
    setHireSuccess(false); // Clear any previous success state
    
    const success = await changeReferralStatusToHired({
      referralId,
      userId: userData.id,
      hiredDate: hireData.hiredDate,
      agreedSalary: hireData.agreedSalary,
      newStatus: ReferralStatus.HIRED,
      totalRewardAmount: pendingHire?.totalAmount,
      baseRewardAmount: pendingHire?.rewardAmount,
      bonusAmount: pendingHire?.bonusAmount,
      hasBonus: pendingHire?.hasBonus,
      onSuccess: (referralId, newStatus) => {
        // Stop loading, show success state
        setIsProcessingHire(false);
        setHireSuccess(true);
        
        // Show success toast with longer duration
        toast({
          title: "🎉 Candidate Hired Successfully!",
          description: `${getCandidateName(selectedReferral!)} has been marked as hired with salary R${hireData.agreedSalary} starting ${hireData.hiredDate}. Rewards have been processed.`,
          variant: "default",
          duration: 5000, // Show for 5 seconds
        });
        
        // Wait 3 seconds before closing both modals to let user see success state
        setTimeout(() => {
          setShowHiredConfirmation(false);
          setPendingHire(null);
          setHireError(null);
          setHireSuccess(false);
          onCloseReferralViewer(); // Close the ReferralViewer modal too
          
          // Call parent success callback
          onStatusChangeSuccess?.(referralId, newStatus, hireData);
        }, 3000);
      },
      onError: (errorMessage) => {
        // Set error state to display in modal
        setIsProcessingHire(false);
        setHireError(errorMessage);
        onError?.(errorMessage);
      }
    });
  };

  const handleAddNote = async (referralId: string, note: string) => {
    if (!note.trim()) return;
    
    try {
      await addRecruiterNote(referralId, note.trim(), userData?.id);
      
      toast({
        title: "Note Added",
        description: "Recruiter note has been successfully added.",
      });
      
      // Notify parent component of successful note addition
      onNoteAddSuccess?.(referralId, note.trim());
      
    } catch (error) {
      console.error('Failed to add note:', error);
      const errorMessage = "Failed to add note. Please try again.";
      
      onError?.(errorMessage);
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  const handleConfirmRejection = async (notes?: string) => {
    if (!pendingRejection) return;

    const { referralId } = pendingRejection;
    
    setIsProcessingRejection(true);
    setRejectionError(null); // Clear any previous errors
    setRejectionSuccess(false); // Clear any previous success state
    
    // Process the rejection with optional notes
    const success = await processStatusChange(referralId, ReferralStatus.REJECTED, notes);
    
    if (success) {
      // Stop loading, show success state
      setIsProcessingRejection(false);
      setRejectionSuccess(true);
      
      // Wait 3 seconds before closing both modals to let user see success state
      setTimeout(() => {
        setShowRejectionWarning(false);
        setPendingRejection(null);
        setRejectionError(null);
        setRejectionSuccess(false);
        onCloseReferralViewer(); // Close the ReferralViewer modal too
      }, 3000);
    } else {
      // Set error state to display in modal (allow retry)
      setIsProcessingRejection(false);
      setRejectionError("Failed to reject candidate. Please try again.");
    }
  };

  const handleCancelRejection = () => {
    if (isProcessingRejection || rejectionSuccess) return; // Prevent closing when processing or showing success
    
    setShowRejectionWarning(false);
    setPendingRejection(null);
    setRejectionError(null); // Clear error when canceling
    setRejectionSuccess(false); // Clear success when canceling
  };

  const handleConfirmHired = (formData: HireFormData) => {
    if (pendingHire) {
      const { referralId } = pendingHire;
      
      // Don't close modal immediately - let processHiredStatusChange handle it
      // This way the modal stays open if there's an error, allowing user to retry
      processHiredStatusChange(referralId, formData);
    }
  };

  const handleCancelHired = () => {
    if (isProcessingHire || hireSuccess) return; // Prevent closing when processing or showing success
    
    setShowHiredConfirmation(false);
    setPendingHire(null);
    setHireError(null); // Clear error when canceling
    setHireSuccess(false); // Clear success when canceling
  };

  return (
    <>
      {/* Referral Viewer Modal */}
      <ReferralViewer
        referral={selectedReferral}
        isOpen={isReferralViewerOpen}
        onClose={onCloseReferralViewer}
        onStatusChange={handleStatusChange}
        onAddNote={handleAddNote}
      />

      {/* Rejection Warning Modal */}
      <RejectionWarningModal
        isOpen={showRejectionWarning}
        candidateName={pendingRejection?.candidateName || null}
        onConfirm={handleConfirmRejection}
        onCancel={handleCancelRejection}
        isProcessing={isProcessingRejection}
        error={rejectionError}
        success={rejectionSuccess}
      />

      {/* Hired Confirmation Modal */}
      <HiredConfirmationModal
        isOpen={showHiredConfirmation}
        candidateName={pendingHire?.candidateName || null}
        jobTitle={pendingHire?.jobTitle}
        rewardAmount={pendingHire?.rewardAmount}
        bonusAmount={pendingHire?.bonusAmount}
        hasBonus={pendingHire?.hasBonus}
        onConfirm={handleConfirmHired}
        onCancel={handleCancelHired}
        isProcessing={isProcessingHire}
        error={hireError}
        success={hireSuccess}
      />
    </>
  );
}
